/**
 * Streamdown custom renderer for data-format code blocks (csv/json/tsv/xml/yaml).
 *
 * Renders the data with a toolbar containing CSV / PDF / DOCX / XLS download
 * buttons that re-run the originating tool server-side via `useDownloadAction`
 * — so the user gets the FULL list (e.g. an org scan with thousands of rows),
 * not just the partial preview the LLM showed in chat.
 *
 * Wired in `chat/DevAgentChatMessageContent.tsx` via:
 *   plugins={{ ..., renderers: [..., buildDataRenderer({ serverDownload, downloadingFormat, ... })] }}
 */
import { useState, useCallback, type MutableRefObject } from "react";
import { useTheme } from "next-themes";
import { Download, Loader2 } from "lucide-react";
import {
  CodeBlockCopyButton,
  CodeBlockExpandToggle,
  CodeBlockCollapsedPreview,
  StreamingIndicator,
} from "./chatMessageCodeBlocks/CodeBlockParts";

interface CustomRendererProps {
  code: string;
  isIncomplete: boolean;
  language: string;
  meta?: string;
}

interface DataRendererCtx {
  isStreaming: boolean;
  showCopyButton: boolean;
  /** From useDownloadAction in DevAgentChatMessageContent — re-runs the originating
   *  tool server-side and streams the FULL formatted file. */
  serverDownload: (format: "csv" | "pdf" | "docx") => Promise<boolean>;
  downloadingFormat: string | null;
}

const DATA_DOWNLOAD_FORMATS: Array<{
  fmt: "csv" | "pdf" | "docx";
  label: string;
  hover: string;
}> = [
  { fmt: "csv", label: ".csv", hover: "hover:text-emerald-400" },
  { fmt: "pdf", label: ".pdf", hover: "hover:text-rose-400" },
  { fmt: "docx", label: ".docx", hover: "hover:text-indigo-400" },
];

function makeDataRenderer(ctxRef: MutableRefObject<DataRendererCtx>) {
  return function DataRenderer({ code, language }: CustomRendererProps) {
    const ctx = ctxRef.current;
    const { resolvedTheme } = useTheme();
    const isDarkMode = resolvedTheme === "dark";
    const [copied, setCopied] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const codeString = code.replace(/\n$/, "");
    const lineCount = codeString.split("\n").length;

    const handleCopy = useCallback(async () => {
      try {
        await navigator.clipboard.writeText(codeString);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.debug("[DataRenderer] copy failed", err);
      }
    }, [codeString]);

    return (
      <div className="my-4 group relative">
        <div
          className={`relative rounded-lg border border-border/50 overflow-hidden ${
            isDarkMode ? "bg-[#0a0a0a]" : "bg-[#fafafa]"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-1 px-3 py-2 border-b border-border/50 bg-muted/30">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {language}
              </span>
              <span className="text-xs text-muted-foreground/80 whitespace-nowrap">
                {lineCount} lines
              </span>
              <StreamingIndicator active={ctx.isStreaming} />
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <CodeBlockCopyButton
                show={ctx.showCopyButton}
                isCopied={copied}
                onCopy={handleCopy}
              />
              {DATA_DOWNLOAD_FORMATS.map(({ fmt, label, hover }) => {
                const isLoading = ctx.downloadingFormat === fmt;
                const isDisabled = ctx.downloadingFormat !== null;
                return (
                  <button
                    key={fmt}
                    type="button"
                    onClick={() => void ctx.serverDownload(fmt)}
                    disabled={isDisabled}
                    title={`Download full result as ${label}`}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-muted-foreground/75 transition-colors ${
                      isDisabled
                        ? "opacity-50 cursor-not-allowed"
                        : `hover:bg-muted/50 ${hover}`
                    }`}
                  >
                    {isLoading ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Download className="w-3 h-3" />
                    )}
                    <span>{label}</span>
                  </button>
                );
              })}
              <CodeBlockExpandToggle
                isExpanded={isExpanded}
                onToggle={() => setIsExpanded((v) => !v)}
              />
            </div>
          </div>

          {!isExpanded && (
            <CodeBlockCollapsedPreview
              codeString={codeString}
              lineCount={lineCount}
              onToggleExpand={() => setIsExpanded(true)}
              previewLabel="Data preview"
              ariaLabel={`Show full data, ${lineCount} lines`}
              showMoreHint={lineCount > 3}
            />
          )}

          {isExpanded && (
            <pre
              className={`overflow-x-auto p-4 text-xs leading-relaxed font-mono max-h-[400px] ${
                isDarkMode ? "text-muted-foreground/90" : "text-foreground/90"
              }`}
            >
              <code>{codeString}</code>
            </pre>
          )}
        </div>
      </div>
    );
  };
}

export function buildDataRenderer(ctxRef: MutableRefObject<DataRendererCtx>) {
  const component = makeDataRenderer(ctxRef);
  return {
    language: ["csv", "tsv", "json", "xml", "yaml", "yml"] as string[],
    component,
  };
}
